import * as React from "react";
import {useTranslation} from "react-i18next";
import {useCustomerContext} from "../customer/CustomerContextProvider/CustomerContextProvider";
import {CheckoutStep} from "./Checkout";
import {useCheckoutViewContext} from "./CheckoutContextProvider/CheckoutViewContextProvider";
import {isAmazonLoggedIn} from "../lib/payments/AmazonPayLibrary";
import {useEffect, useState} from "react";
import window from "../lib/WindowExtended";
import styles from "./Breadcrumb.module.scss";

type CrumbProps = {
  title: string;
  view: CheckoutStep;
  state: BreadcrumbState;
  disabled: boolean;
};

enum BreadcrumbState {
  ACTIVE = "active",
  DONE = "done",
  PENDING = "pending",
}

const Breadcrumb: React.FC = () => {
  const checkoutSteps = [
    {title: "Login", step: CheckoutStep.LOGIN},
    {title: "Adresse", step: CheckoutStep.ADDRESS},
    {title: "Versand", step: CheckoutStep.SHIPPING},
    {title: "Zahlung", step: CheckoutStep.PAYMENT},
    {title: "Prüfen", step: CheckoutStep.SUMMARY},
  ];
  const {t} = useTranslation("checkout");
  const checkoutView = useCheckoutViewContext();
  const {gotoStep, step} = checkoutView;
  const {customer} = useCustomerContext();
  const stepIndex = checkoutSteps.findIndex((checkoutStep) => checkoutStep.step === step);
  const [touchedSteps, setTouchedSteps] = useState<Set<CheckoutStep>>(new Set());

  useEffect(() => {
    setTouchedSteps((prevState) => {
      checkoutSteps.forEach((checkoutStep, index) => {
        if (index <= stepIndex && index !== 4) {
          prevState.add(checkoutStep.step);
        }
      });

      return prevState;
    });
  }, [step]);

  // reset touched steps after cancel amazon pay
  useEffect(() => {
    if (!window.orderReferenceId) {
      const touchedStepsSet = new Set<CheckoutStep>();
      checkoutSteps.forEach((checkoutStep, index) => {
        if (index <= stepIndex && index !== 4) {
          touchedStepsSet.add(checkoutStep.step);
        }
      });
      setTouchedSteps(touchedStepsSet);
    }
  }, [window.orderReferenceId]);

  const Crumb: React.FC<CrumbProps> = ({title, state, view, disabled}) => {
    return (
      <div
        className={`${styles.crumbWrapper} ${styles[state]} ${disabled ? styles.inactive : ""}`}
        onClick={() => {
          if (!disabled && touchedSteps.has(view)) {
            gotoStep(view);
          }
        }}
      >
        <div className={`${styles.title}`}>
          <span>{t(title)}</span>
        </div>
        <div className={styles.arrowRight} />
      </div>
    );
  };

  return (
    <div className={`${styles.breadCrumbWrapper} reduced-width`}>
      {checkoutSteps.map((checkoutStep, index) => {
        const breadcrumbDisabled =
          checkoutStep.step === CheckoutStep.LOGIN && (customer.isLoggedIn || isAmazonLoggedIn());

        let state: BreadcrumbState;
        if (stepIndex === index) {
          state = BreadcrumbState.ACTIVE;
        } else if (stepIndex > index || touchedSteps.has(checkoutStep.step)) {
          state = BreadcrumbState.DONE;
        } else {
          state = BreadcrumbState.PENDING;
        }

        return (
          <Crumb
            key={index}
            disabled={breadcrumbDisabled}
            title={checkoutStep.title}
            view={checkoutStep.step}
            state={state}
          />
        );
      })}
    </div>
  );
};

export default Breadcrumb;
